/**
 * White Label Branding Configuration
 *
 * The backend's white-label-clients row is the source of truth for branding; the static
 * entries below are the request-hostname fallback that keeps the first paint correct for
 * known clients and keeps branding alive when the backend is unreachable. At runtime the
 * app hydrates the backend row over the static entry (see applyRemoteWhiteLabelConfig).
 */

import LoadingSpinner from 'icons/LoadingSpinner';
import React from 'react';

import type { WhiteLabelClient } from 'services/whiteLabelApi';

export interface WhiteLabelConfig {
  /**
   * Hostnames this client serves, lowercase and without port. Resolved from the incoming
   * request so a single build can serve every white-label domain.
   */
  domains: string[];
  vercelProjectId: string;
  companyName: string;
  logoUrl: string;
  logoWithNameUrl: string;
  LoaderComponent?: React.FC;
  primaryColor?: string;
  description?: string;
  favicons: {
    favicon16: string;
    favicon32: string;
  };
  teamTerm?: string; // Custom term for "Team" (e.g., "Company" for Sagan)
}

// cspell: disable-next-line
export const WHITELABEL_CONFIGS: Record<string, WhiteLabelConfig> = {
  // cspell: disable-next-line
  generalstaffing: {
    // cspell: disable-next-line
    vercelProjectId: 'prj_lWqEfR7QkUGWXNCy7al8ymOUw0qU',
    domains: ['workspace.generalstaffing.com', 'general-staffing-mocha.vercel.app'],
    companyName: 'General Staffing',
    logoUrl: '/clients/general-staffing-logo.svg',
    logoWithNameUrl: '/clients/general-staffing-logo-with-name.svg',
    LoaderComponent: LoadingSpinner,
    description: "Hire, pay, and manage your distributed workforce from General Staffing's HR platform.",
    favicons: {
      favicon16: '/clients/general-staffing-favicon-16.ico?v=32',
      favicon32: '/clients/general-staffing-favicon-32.ico?v=32',
    },
  },
  // cspell: disable-next-line
  globalpaydirect: {
    // cspell: disable-next-line
    vercelProjectId: 'prj_WDIFr82M1vFLeEuzA7YOt78sndBC',
    domains: ['people.globalpaydirectllc.com'],
    companyName: 'Global Pay Direct',
    // cspell: disable-next-line
    logoUrl: '/clients/globalpaydirect-logo.png',
    // cspell: disable-next-line
    logoWithNameUrl: '/clients/globalpaydirect-logo-with-name.png',
    LoaderComponent: LoadingSpinner,
    description: "Hire, pay, and manage your distributed workforce from GlobalPayDirect's HR platform.",
    favicons: {
      // cspell: disable-next-line
      favicon16: '/clients/globalpaydirect-favicon-16.ico?v=32',
      // cspell: disable-next-line
      favicon32: '/clients/globalpaydirect-favicon-32.ico?v=32',
    },
    teamTerm: 'Company',
  },

  // cspell: disable-next-line
  remoteglobalpayroll: {
    vercelProjectId: 'prj_32EB1bvcHjWS8vy9Ebm5vH6Yv5z3',
    domains: ['staff.remotelatinos.com', 'thera-remote-latinos.vercel.app'],
    companyName: 'Remote Global',
    logoUrl: '/clients/remote-latino-logo.png',
    logoWithNameUrl: '/clients/remote-latino-logo-name.png',
    LoaderComponent: LoadingSpinner,
    description: 'A simple way to pay and manage your distributed team.',
    favicons: {
      favicon16: '/clients/remote-latino-favicon-16x16.png?v=32',
      favicon32: '/clients/remote-latino-favicon-32x32.png?v=32',
    },
  },

  // cspell:disable
  awana: {
    vercelProjectId: 'prj_jZOo8XB1BcbCaLzDUFPMULrhQWAa',
    domains: ['app.awana.io', 'awana-tawny.vercel.app'],
    companyName: 'Awana',
    logoUrl: '/clients/awana-logo.png',
    logoWithNameUrl: '/clients/awana-logo-with-name.png',
    LoaderComponent: LoadingSpinner,
    primaryColor: '#002254',
    description:
      'Awana is your end-to-end platform to hire, manage, and pay top technical talent across the world — from sourcing and onboarding to contracts, compliance, and monthly payouts, all in one place.',
    favicons: {
      favicon16: '/clients/awana-favicon-16.png?v=1',
      favicon32: '/clients/awana-favicon-32.png?v=1',
    },
  },

  initiocapital: {
    vercelProjectId: 'prj_pOc337N83gMqOwZ4DPRGJpMhOes1',
    domains: ['payroll.initioraise.com', 'initio-sand.vercel.app'],
    companyName: 'Initio Capital',
    logoUrl: '/clients/initio-logo.png',
    logoWithNameUrl: '/clients/initio-logo-with-name.jpg',
    LoaderComponent: LoadingSpinner,
    description: 'We help founders on their business journey.',
    favicons: {
      favicon16: '/clients/initio-favicon-16.png?v=1',
      favicon32: '/clients/initio-favicon-32.png?v=1',
    },
  },

  englishspeakingjobs: {
    vercelProjectId: 'prj_ZPbVYdsub8AtxaS4JUqvUzqQEfbf',
    domains: ['app.englishspeakingjobs.com', 'englishspeakingjobs.vercel.app'],
    companyName: 'English Speaking Jobs',
    logoUrl: '/clients/englishspeakingjobs-logo.png',
    logoWithNameUrl: '/clients/englishspeakingjobs-logo-with-name.svg',
    LoaderComponent: LoadingSpinner,
    primaryColor: '#298D2F',
    description:
      'EnglishSpeakingJobs.com is a self-service global hiring platform that connects employers with pre-vetted, English-speaking professionals from around the world.',
    favicons: {
      favicon16: '/clients/englishspeakingjobs-favicon-16.png?v=1',
      favicon32: '/clients/englishspeakingjobs-favicon-32.png?v=1',
    },
  },
  // cspell:enable

  // Add more white label clients here:
  // 'client2': {
  //   branches: ['staging-client2', 'production-client2'],
  //   companyName: 'Client 2 Name',
  //   logoUrl: '/logos/client2-logo.svg',
  //   logoWithNameUrl: '/logos/client2-logo-with-name.svg',
  //   description: 'Custom description for Client 2.',
  // },
};

/**
 * Hostnames arrive with mixed casing, an optional port, and sometimes a trailing dot on a
 * fully qualified name. Normalise all three before matching.
 */
function normalizeHost(host: string): string {
  return host.trim().toLowerCase().split(':')[0].replace(/\.$/, '');
}

// Built once. getWhiteLabelPageTitle alone runs on the order of a few hundred times per render,
// so resolution has to be a map lookup rather than a scan over every client.
const CONFIG_BY_DOMAIN = new Map<string, WhiteLabelConfig>();
const CONFIG_BY_VERCEL_PROJECT_ID = new Map<string, WhiteLabelConfig>();

for (const config of Object.values(WHITELABEL_CONFIGS)) {
  CONFIG_BY_VERCEL_PROJECT_ID.set(config.vercelProjectId, config);
  for (const domain of config.domains) {
    const host = normalizeHost(domain);
    const clash = CONFIG_BY_DOMAIN.get(host);
    if (clash && clash !== config) {
      // Two clients claiming one hostname would silently serve the wrong brand, so make it loud.
      console.error(
        `White label config error: "${host}" is claimed by both "${clash.companyName}" and "${config.companyName}".`,
      );
    }
    CONFIG_BY_DOMAIN.set(host, config);
  }
}

// Backend branding for the current browser hostname, merged over the static entry.
// Client-side only: the override is keyed to window.location.hostname.
let remoteOverride: WhiteLabelConfig | null = null;

/**
 * Resolve branding for an explicit hostname. Use this wherever the real request is in hand
 * (middleware, and anywhere else running per request on the server).
 */
export function getWhiteLabelConfigForHost(host: string | null | undefined): WhiteLabelConfig | null {
  if (!host) {
    return null;
  }
  const normalized = normalizeHost(host);
  if (remoteOverride && typeof window !== 'undefined' && normalized === normalizeHost(window.location.hostname)) {
    return remoteOverride;
  }
  return CONFIG_BY_DOMAIN.get(normalized) ?? null;
}

let cachedBrowserConfig: WhiteLabelConfig | null | undefined;

/**
 * Resolve branding without an explicit request.
 *
 * In the browser the hostname is authoritative, which is what lets one build serve every
 * white-label domain. Server rendering has no ambient hostname, so it falls back to the
 * build-time project id. Both keys live on the same config entry, so the two paths cannot
 * disagree for a per-project build, and the fallback also keeps preview deployments branded
 * on their generated *.vercel.app hostnames.
 */
export function getCurrentWhiteLabelConfig(): WhiteLabelConfig | null {
  if (typeof window !== 'undefined') {
    if (cachedBrowserConfig === undefined) {
      cachedBrowserConfig = getWhiteLabelConfigForHost(window.location.hostname);
    }
    if (cachedBrowserConfig) {
      return cachedBrowserConfig;
    }
  }

  const vercelProjectId = process.env.NEXT_PUBLIC_VERCEL_PROJECT_ID;
  return vercelProjectId ? (CONFIG_BY_VERCEL_PROJECT_ID.get(vercelProjectId) ?? null) : null;
}

/**
 * Resolve branding, preferring an explicit request hostname and falling back to the ambient
 * build-time identity when that hostname is not a configured domain.
 *
 * The fallback is what keeps preview deployments of a white-label project branded: their
 * generated hostnames are not in any client's `domains`, but the build still carries the
 * project id.
 */
export function resolveWhiteLabelConfig(host?: string | null): WhiteLabelConfig | null {
  return getWhiteLabelConfigForHost(host) ?? getCurrentWhiteLabelConfig();
}

/**
 * Whether the request is being served under a white-label brand. Pass the request hostname
 * where one is available; omit it in client code.
 */
export function isWhiteLabeled(host?: string | null): boolean {
  return resolveWhiteLabelConfig(host) !== null;
}

/**
 * Branding fields consumed from the backend's white-label-clients row. Derived with Pick
 * so an SDK field rename fails the build here instead of silently reading undefined and
 * dropping backend branding. The asset URLs are short-lived signed URLs, freshly minted
 * on every response, so they are applied per page load and never persisted.
 */
export type RemoteWhiteLabelBranding = Pick<
  WhiteLabelClient,
  | 'companyName'
  | 'description'
  | 'logoUrl'
  | 'logoWithNameUrl'
  | 'favicon16Url'
  | 'favicon32Url'
  | 'teamTerm'
  | 'primaryColor'
  | 'vercelProjectId'
>;

function trimmedOrUndefined(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Merge the backend's branding row for the current browser hostname over the static entry
 * and make it the effective config. Static values fill any field the row is missing, which
 * also covers build-time-only fields like LoaderComponent. Returns true when the effective
 * branding changed so callers can trigger a re-render. No-op during SSR.
 */
export function applyRemoteWhiteLabelConfig(remote: RemoteWhiteLabelBranding): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = normalizeHost(window.location.hostname);
  const base = CONFIG_BY_DOMAIN.get(host) ?? null;

  // trimmedOrUndefined also treats an empty string from the backend as missing, so a
  // blank field falls back per-field instead of failing the completeness guard below.
  const companyName = trimmedOrUndefined(remote.companyName) ?? base?.companyName;
  const logoUrl = trimmedOrUndefined(remote.logoUrl) ?? base?.logoUrl;
  const logoWithNameUrl = trimmedOrUndefined(remote.logoWithNameUrl) ?? base?.logoWithNameUrl ?? logoUrl;
  const favicon16 = trimmedOrUndefined(remote.favicon16Url) ?? base?.favicons.favicon16;
  const favicon32 = trimmedOrUndefined(remote.favicon32Url) ?? base?.favicons.favicon32;
  // An incomplete row with no static entry to fill the gaps cannot brand the app.
  if (!companyName || !logoUrl || !logoWithNameUrl || !favicon16 || !favicon32) {
    return false;
  }

  const merged: WhiteLabelConfig = {
    domains: base?.domains ?? [host],
    vercelProjectId: trimmedOrUndefined(remote.vercelProjectId) ?? base?.vercelProjectId ?? '',
    companyName,
    logoUrl,
    logoWithNameUrl,
    LoaderComponent: base?.LoaderComponent ?? LoadingSpinner,
    primaryColor: trimmedOrUndefined(remote.primaryColor) ?? base?.primaryColor,
    description: trimmedOrUndefined(remote.description) ?? base?.description,
    favicons: { favicon16, favicon32 },
    teamTerm: trimmedOrUndefined(remote.teamTerm) ?? base?.teamTerm,
  };

  const current = getWhiteLabelConfigForHost(window.location.hostname);
  const changed =
    current?.companyName !== merged.companyName ||
    current.logoUrl !== merged.logoUrl ||
    current.logoWithNameUrl !== merged.logoWithNameUrl ||
    current.primaryColor !== merged.primaryColor ||
    current.description !== merged.description ||
    current.teamTerm !== merged.teamTerm ||
    current.favicons.favicon16 !== merged.favicons.favicon16 ||
    current.favicons.favicon32 !== merged.favicons.favicon32;

  remoteOverride = merged;
  cachedBrowserConfig = undefined;
  return changed;
}

/**
 * Drop the backend override so branding reverts to the static entry. Used when the row
 * disappears or the backend stays unreachable long enough for the override's signed
 * asset URLs to expire. Returns true when an override was actually removed.
 */
export function clearRemoteWhiteLabelConfig(): boolean {
  if (remoteOverride === null) {
    return false;
  }
  remoteOverride = null;
  cachedBrowserConfig = undefined;
  return true;
}
